import React, { useState, useEffect } from 'react';
import { Search, Users, Eye } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useLoader } from '../../components/shared/LoaderContext';

export default function DirectoryPage() {
  const [directory, setDirectory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedMember, setSelectedMember] = useState(null);
  const { showLoader, hideLoader } = useLoader();
  const limit = 20;

  const fetchDirectory = async (searchVal = search, pageVal = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pageVal, limit });
      if (searchVal) params.append('search', searchVal);
      const res = await api.get(`/directory?${params}`);
      setDirectory(res.data.directory || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to fetch member directory');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDirectory(); }, [page]);

  const handleSearch = async () => {
    setPage(1);
    const interval = showLoader('Searching directory...');
    await fetchDirectory(search, 1);
    hideLoader(interval);
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') handleSearch(); };

  const handleClear = () => {
    setSearch('');
    setPage(1);
    fetchDirectory('', 1);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div>
      <div className="page-header">
        <h2>Member Directory</h2>
        <p>Contact directory of all active members of Bombay Psychiatric Society</p>
      </div>

      {/* Search Bar */}
      <div className="card" style={{ padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <Search size={15} style={{
              position: 'absolute', left: 12, top: '50%',
              transform: 'translateY(-50%)', color: 'var(--text-muted)'
            }} />
            <input className="form-input"
              placeholder="Search by name, city, degree or member ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              style={{ paddingLeft: 36 }} />
          </div>
          <button className="btn btn-primary btn-sm" onClick={handleSearch}>
            <Search size={14} /> Search
          </button>
          {search && (
            <button className="btn btn-outline btn-sm" onClick={handleClear}>
              Clear
            </button>
          )}
        </div>
        {total > 0 && (
          <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-muted)' }}>
            {total} active member(s) found
          </div>
        )}
      </div>

      {/* Directory Table */}
      <div className="card">
        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : directory.length === 0 ? (
          <div className="empty-state">
            <Users size={40} />
            <p>No active members found</p>
            {search && <p style={{ fontSize: 13 }}>Try a different search term</p>}
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Member ID</th>
                  <th>Full Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>City</th>
                  {/* <th>State</th> */}
                  <th>Degree</th>
                  <th>Practice Address</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {directory.map((m) => (
                  <tr key={m.memberId}>
                    <td>
                      <span style={{
                        fontWeight: 700, color: 'var(--primary)',
                        fontFamily: 'monospace', fontSize: 13
                      }}>
                        {m.memberId}
                      </span>
                    </td>
                    <td style={{ fontWeight: 500 }}>{m.name}</td>
                    <td style={{ fontSize: 13, color: 'var(--text-muted)' }}>{m.email || '—'}</td>
                    <td style={{ fontSize: 13 }}>{m.phone || '—'}</td>
                    <td style={{ fontSize: 13 }}>{m.city || '—'}</td>
                    {/* <td style={{ fontSize: 13 }}>{m.state || '—'}</td> */}
                    <td style={{ fontSize: 13 }}>{m.degree || '—'}</td>
                    <td style={{ fontSize: 13 }}>{m.officeAddress || '—'}</td>
                    <td>
                      <button className="btn btn-outline btn-sm" onClick={() => setSelectedMember(m)}>
                        <Eye size={13} /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal — shows Membership Type and full info */}
      {selectedMember && (
        <div onClick={() => setSelectedMember(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(15,36,64,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: 20
        }}>
          <div onClick={(e) => e.stopPropagation()} className="card" style={{
            padding: 28, maxWidth: 420, width: '100%'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
              <div>
                <h3 style={{ color: 'var(--primary)', fontSize: 18 }}>{selectedMember.name}</h3>
                <span style={{ fontWeight: 700, color: 'var(--primary)', fontFamily: 'monospace', fontSize: 13 }}>
                  {selectedMember.memberId}
                </span>
              </div>
              <button onClick={() => setSelectedMember(null)} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 20, color: 'var(--text-muted)', lineHeight: 1
              }}>×</button>
            </div>
            {[
              { label: 'Email', value: selectedMember.email },
              { label: 'Phone', value: selectedMember.phone },
              { label: 'City', value: selectedMember.city },
              { label: 'State', value: selectedMember.state },
              { label: 'Degree', value: selectedMember.degree },
              { label: 'University', value: selectedMember.university },
              { label: 'Membership Type', value: selectedMember.membershipType },
              { label: 'Areas of Expertise', value: selectedMember.areasOfInterest },
            ].map((row) => row.value && (
              <div key={row.label} style={{
                display: 'flex', gap: 12, padding: '8px 0',
                borderBottom: '1px solid var(--border)'
              }}>
                <div style={{ width: 140, color: 'var(--text-muted)', fontSize: 13, flexShrink: 0 }}>{row.label}</div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>
                  {row.label === 'Membership Type' ? (
                    <span style={{
                      background: '#dbeafe', color: '#1e40af',
                      padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600
                    }}>{row.value}</span>
                  ) : row.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex', justifyContent: 'center', gap: 8,
            padding: '16px', borderTop: '1px solid var(--border)'
          }}>
            <button className="btn btn-outline btn-sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}>← Prev</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button key={p}
                className={`btn btn-sm ${p === page ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setPage(p)}>{p}</button>
            ))}
            <button className="btn btn-outline btn-sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}>Next →</button>
          </div>
        )}
      </div>
    
  );
}
